// Client-side PDF page counts using the already-bundled pdf.js 1.10.100
// (shipped inside pdf-parse). Its UMD build is browser-safe: every Node
// require (fs/http/https/url/zlib/node-ensure) sits behind isNodeJS() or
// `typeof window === "undefined"` guards. We load it lazily as a classic
// script (Vite `?url` asset), read the global `pdfjsDistBuildPdf`, and point
// `PDFJS.workerSrc` at the bundled worker. No new dependency, no regex parsing,
// and no cross-user cache: each request fetches with the current session.
import { useEffect, useState } from "react";
import pdfScriptUrl from "pdf-parse/lib/pdf.js/v1.10.100/build/pdf.js?url";
import pdfWorkerUrl from "pdf-parse/lib/pdf.js/v1.10.100/build/pdf.worker.js?url";
import { safeMediaUrl } from "./MediaStrip.jsx";

export class PdfPageCountError extends Error {}

// Library code only (no user data), so a module-level load promise is safe.
let libraryPromise = null;
export function loadBrowserPdfJs(doc = typeof document !== "undefined" ? document : null) {
  if (typeof window !== "undefined" && window.pdfjsDistBuildPdf) return Promise.resolve(configure(window.pdfjsDistBuildPdf));
  if (!doc) return Promise.reject(new PdfPageCountError("PDF reader unavailable."));
  if (!libraryPromise) {
    libraryPromise = new Promise((resolve, reject) => {
      const script = doc.createElement("script");
      script.src = pdfScriptUrl;
      script.async = true;
      script.dataset.placesPdfjs = "1.10.100";
      script.onload = () => window.pdfjsDistBuildPdf ? resolve(configure(window.pdfjsDistBuildPdf)) : reject(new PdfPageCountError("PDF reader did not initialize."));
      script.onerror = () => reject(new PdfPageCountError("PDF reader failed to load."));
      doc.head.appendChild(script);
    }).catch(err => { libraryPromise = null; throw err; });
  }
  return libraryPromise;
}
function configure(lib) {
  if (lib?.PDFJS) {
    lib.PDFJS.workerSrc = pdfWorkerUrl;
    lib.PDFJS.isEvalSupported = false; // never compile font programs
    lib.PDFJS.verbosity = 0;
  }
  return lib;
}

// Real parser path: pdf.js resolves the xref (tables or compressed streams,
// incremental revisions) and reports the page tree's numPages.
export async function countPdfPages(bytes, lib) {
  if (!lib?.getDocument) throw new PdfPageCountError("PDF reader unavailable.");
  const data = bytes instanceof Uint8Array ? bytes : new Uint8Array(bytes);
  const task = lib.getDocument({ data, disableAutoFetch: true, disableStream: true, disableFontFace: true });
  let doc;
  try {
    doc = await task.promise;
    const pages = doc.numPages;
    if (!Number.isInteger(pages) || pages < 1) throw new PdfPageCountError("Invalid page count.");
    return pages;
  } catch (err) {
    throw err instanceof PdfPageCountError ? err : new PdfPageCountError(err?.message || "Unreadable PDF.");
  } finally {
    try { doc ? doc.destroy() : task.destroy?.(); } catch { /* best effort */ }
  }
}

// Fetches with the current session every time (no shared URL cache).
export async function fetchPdfPageCount(url, { fetchImpl = globalThis.fetch, loadLib = loadBrowserPdfJs, signal } = {}) {
  const safe = safeMediaUrl(url);
  if (!safe) throw new PdfPageCountError("No readable file URL.");
  const response = await fetchImpl(safe, { credentials: "same-origin", cache: "no-store", signal });
  if (!response.ok) throw new PdfPageCountError(`File read failed (${response.status}).`);
  const bytes = new Uint8Array(await response.arrayBuffer());
  return countPdfPages(bytes, await loadLib());
}

let hookOptions = {};
// Test seam: inject fetch/loader without touching the network or DOM.
export function setPdfPageCountOptions(options = {}) { hookOptions = options; }

// Lazy: starts only once `enabled` (tile visible / viewer open). Aborted on
// unmount or URL change so a stale read never lands on another item.
export function usePdfPageCount(item, enabled = true) {
  const url = item?.type === "pdf" ? item.url : null;
  const [state, setState] = useState({ status: "idle", pages: null });
  useEffect(() => {
    if (!url || !enabled) return undefined;
    let active = true;
    const controller = typeof AbortController !== "undefined" ? new AbortController() : null;
    setState({ status: "loading", pages: null });
    fetchPdfPageCount(url, { ...hookOptions, signal: controller?.signal })
      .then(pages => { if (active) setState({ status: "ok", pages }); })
      .catch(() => { if (active) setState({ status: "error", pages: null }); });
    return () => { active = false; controller?.abort(); };
  }, [url, enabled]);
  return state;
}

export function pageCountText(state) {
  if (state.status === "ok") return `${state.pages} ${state.pages === 1 ? "page" : "pages"}`;
  if (state.status === "error") return "Page count unavailable";
  return "Counting pages…";
}
